import React from 'react';
import { Typography, Tabs, Divider } from 'antd';
import { CreativityType, Resolutions } from '../../../types/banner';
import { useTemplateInfo } from '../../../hooks/banners/use-template-utils';
import { getIconsForResolution } from './get-icons';
import { extractResolutionFromUrl } from '../../../utils';
import { RESOLUTION_CATEGORIES, RESOLUTION_DISPLAY_NAMES, RESOLUTIONS_ORDER } from '../../../constants/resolutions';
import { AdChannels } from '../../../types/ad-channels';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

const { Title } = Typography;

interface DemoPreviewProps {
  creativityType: CreativityType;
}

export const DemoPreview: React.FC<DemoPreviewProps> = ({ creativityType }) => {
  const { spacing, fontSize, screens } = useResponsiveSizes();
  const { previewVideo: defaultVideo, videoVariants, thumbnail } = useTemplateInfo(creativityType);
  const [selectedCategory, setSelectedCategory] = React.useState<AdChannels>('social');

  const resolutions = React.useMemo(() => {
    return RESOLUTIONS_ORDER.filter(res => Object.keys(videoVariants).includes(res));
  }, [videoVariants]);

  const [selectedResolution, setSelectedResolution] = React.useState<Resolutions | null>(() => {
    return resolutions.length > 0 ? resolutions[0] : null;
  });

  const previewVideo = selectedResolution ? videoVariants[selectedResolution] : defaultVideo;

  const categorizedResolutions = React.useMemo(() => {
    const categories: Record<AdChannels, Resolutions[]> = {
      social: [],
      display: [],
      DOOH: []
    };

    resolutions.forEach(res => {
      const channelsList = RESOLUTION_CATEGORIES[res as Resolutions];
      if (channelsList) {
        channelsList.forEach(channel => {
          categories[channel].push(res as Resolutions);
        });
      }
    });

    return categories;
  }, [resolutions]);

  const getButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    height: 30,
    padding: '0px 20px',
    border: isSelected ? 'none' : '1px solid #d1d5db',
    borderRadius: 24,
    background: isSelected ? 'rgb(72 114 207)' : 'white',
    color: isSelected ? 'white' : '#374151',
    fontSize: fontSize.base,
    fontWeight: isSelected ? 500 : 400,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    boxShadow: isSelected ? '0 4px 12px rgba(37, 99, 235, 0.4)' : 'none'
  });

  return (
    <div
      style={{
        position: 'sticky',
        top: 16,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start'
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Title style={{ margin: `${spacing.sm}px 0`, fontSize: fontSize.xxl }}>Demo</Title>
      </div>

      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: spacing.xs,
          padding: screens.xs ? spacing.sm : spacing.md
        }}
      >
        {/* Tabs de Categorías con Chips */}
        {resolutions.length > 0 && (
          <div style={{ width: '100%', marginBottom: screens.xs ? spacing.md : spacing.lg }}>
            <Tabs
              activeKey={selectedCategory}
              onChange={key => {
                setSelectedCategory(key as AdChannels);
                const newCategory = key as AdChannels;
                if (!categorizedResolutions[newCategory].includes((selectedResolution as Resolutions) || '')) {
                  setSelectedResolution(categorizedResolutions[newCategory][0]);
                }
              }}
              items={[
                ...(categorizedResolutions.social.length > 0
                  ? [
                      {
                        key: 'social',
                        label: <span style={{ fontSize: fontSize.base, fontWeight: 500 }}>Social Media</span>,
                        children: (
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              paddingTop: 8
                            }}
                          >
                            {categorizedResolutions.social.map(res => {
                              const label = RESOLUTION_DISPLAY_NAMES[res] || res;
                              const isSelected = selectedResolution === res;

                              return (
                                <button
                                  key={res}
                                  onClick={() => setSelectedResolution(res)}
                                  style={getButtonStyle(isSelected)}
                                  onMouseEnter={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#3076cc';
                                      e.currentTarget.style.backgroundColor = '#eff6ff';
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#d1d5db';
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {label.replace(' | ', '')}
                                </button>
                              );
                            })}
                          </div>
                        )
                      }
                    ]
                  : []),
                ...(categorizedResolutions.display.length > 0
                  ? [
                      {
                        key: 'display',
                        label: <span style={{ fontSize: fontSize.base, fontWeight: 500 }}>Medios digitales</span>,
                        children: (
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              paddingTop: 8
                            }}
                          >
                            {categorizedResolutions.display.map(res => {
                              // Usar el nombre mapeado por defecto
                              let label = RESOLUTION_DISPLAY_NAMES[res] || res;

                              // Si estamos en la tab Medios Digitales (display)
                              // y la resolución es 1920x1080 (que normalmente se muestra como 16:9),
                              // mostramos el texto original sin aplicar el mapeo
                              if (selectedCategory === 'display' && res === '1920x1080') {
                                label = '1920x1080';
                              }

                              const isSelected = selectedResolution === res;

                              return (
                                <button
                                  key={res}
                                  onClick={() => setSelectedResolution(res)}
                                  style={getButtonStyle(isSelected)}
                                  onMouseEnter={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#60a5fa';
                                      e.currentTarget.style.backgroundColor = '#eff6ff';
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#d1d5db';
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {label.replace(' | ', '')}
                                </button>
                              );
                            })}
                          </div>
                        )
                      }
                    ]
                  : []),
                ...(categorizedResolutions.DOOH.length > 0
                  ? [
                      {
                        key: 'DOOH',
                        label: <span style={{ fontSize: fontSize.base, fontWeight: 500 }}>DOOH</span>,
                        children: (
                          <div
                            style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap',
                              justifyContent: 'center',
                              paddingTop: 8
                            }}
                          >
                            {categorizedResolutions.DOOH.map(res => {
                              const label = RESOLUTION_DISPLAY_NAMES[res] || res;
                              const isSelected = selectedResolution === res;

                              return (
                                <button
                                  key={res}
                                  onClick={() => setSelectedResolution(res)}
                                  style={getButtonStyle(isSelected)}
                                  onMouseEnter={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#34d399';
                                      e.currentTarget.style.backgroundColor = '#ecfdf5';
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#d1d5db';
                                      e.currentTarget.style.backgroundColor = 'white';
                                    }
                                  }}
                                >
                                  {label.replace(' | ', '')}
                                </button>
                              );
                            })}
                          </div>
                        )
                      }
                    ]
                  : [])
              ]}
              style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            />
          </div>
        )}

        {/* Video Preview */}
        {previewVideo ? (
          <video
            src={previewVideo}
            controls
            muted
            loop
            style={{
              width: '100%',
              maxWidth: screens.xs ? 280 : screens.sm ? 320 : screens.md ? 380 : 420,
              height: 'auto',
              borderRadius: 8,
              outline: 'none'
            }}
          />
        ) : thumbnail ? (
          <img
            src={thumbnail}
            alt='Thumbnail'
            style={{
              width: '100%',
              maxWidth: screens.xs ? 280 : screens.sm ? 320 : screens.md ? 380 : 420,
              height: 'auto',
              borderRadius: 8
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              maxWidth: screens.xs ? 280 : screens.sm ? 320 : screens.md ? 380 : 420,
              height: screens.xs ? 200 : screens.sm ? 240 : 280,
              backgroundColor: '#eaeaea',
              border: '1px dashed #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#a0a0a0',
              fontSize: fontSize.base
            }}
          >
            Sin preview de video
          </div>
        )}

        {/* Info de Resolución con Iconos */}
        {previewVideo && (
          <div style={{ textAlign: 'center', marginTop: spacing.md }}>
            <div style={{ fontSize: fontSize.base, color: '#6b7280', marginBottom: spacing.sm }}>
              Resolución actual:{' '}
              <strong style={{ fontWeight: 700, color: '#4B5563' }}>
                {selectedResolution || extractResolutionFromUrl(previewVideo) || 'Default'} px
              </strong>
            </div>

            {/* Separador */}
            <Divider style={{ margin: `${spacing.sm}px 0` }} />

            {/* Título centrado arriba de los iconos */}
            <div
              style={{
                fontSize: fontSize.base,
                fontStyle: 'italic',
                color: '#6b7280',
                textAlign: 'center',
                marginBottom: spacing.sm
              }}
            >
              Plataformas sugeridas
            </div>

            {/* Iconos de plataformas */}
            {selectedResolution && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: spacing.xs,
                  marginTop: spacing.xs
                }}
              >
                {getIconsForResolution(selectedResolution)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
